import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { logout } from '../actions/userActions';
import { ThemeToggle } from '../ui/Brand';
import '../assests/glass-theme.css';

/* ── helpers ──────────────────────────────────────────────── */

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');

const maskPhone = (phone = '') => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) return phone;
  return `+91 ${digits.slice(-10, -6).replace(/./g, '•')} ${digits.slice(-6, -4)}XX XX`;
};

/* ── component ────────────────────────────────────────────── */

const PlayerSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userInfo, setUserInfo] = useState({});
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  /* Load player info from localStorage */
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('userInfo') || '{}');
      setUserInfo(stored);
    } catch {
      setUserInfo({});
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch('/api/users/verification-status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!data?.success) return;
        setUserInfo((current) => {
          const updated = {
            ...current,
            verification: data.verification || current.verification,
            referralCode: data.referralCode || current.referralCode,
          };
          localStorage.setItem('userInfo', JSON.stringify(updated));
          return updated;
        });
      })
      .catch(() => {});
  }, []);

  const playerName    = userInfo?.name  || userInfo?.fullName || 'Player';
  const playerPhone   = userInfo?.phone || userInfo?.phoneNumber || '';
  const playerInitials = getInitials(playerName) || 'P';
  const referralCode  = userInfo?.referralCode || userInfo?.inviteCode || '—';

  const isFullyVerified =
    userInfo?.verification?.isFullyVerified ||
    ((userInfo?.verification?.aadhaar?.verified || userInfo?.verification?.pan?.verified) &&
      userInfo?.verification?.manualKYC?.status === 'approved');

  const isUnderReview =
    !isFullyVerified &&
    (userInfo?.verification?.aadhaar?.pending ||
      userInfo?.verification?.pan?.pending ||
      userInfo?.verification?.manualKYC?.status === 'pending');
  const referralUnlocked = isFullyVerified && referralCode !== '—';

  /* Verification badge colour */
  const verBadgeStyle = isFullyVerified
    ? settingsStyles.badgeVerified
    : isUnderReview
      ? settingsStyles.badgeReview || { ...settingsStyles.badgePending, backgroundColor: 'var(--cm-warning)', color: '#fff' }
      : settingsStyles.badgePending;
  const verBadgeLabel = isFullyVerified ? 'VERIFIED' : isUnderReview ? 'UNDER REVIEW' : 'PENDING';

  /* ── handlers ─────────────────────────────────── */

  const handleCopyReferral = () => {
    if (!referralUnlocked) {
      toast.info('Complete KYC and your first Security club check-in to unlock your referral code.');
      return;
    }
    if (!referralCode || referralCode === '—') return;
    navigator.clipboard.writeText(referralCode).then(() => {
      setReferralCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setReferralCopied(false), 2500);
    });
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/player-login', { replace: true });
  };

  /* ── render helpers ───────────────────────────── */

  const SettingsRow = ({ icon, label, value, badge, badgeStyle, onClick, rightIcon, danger }) => (
    <button
      className="settings-item"
      onClick={onClick}
      style={{
        ...settingsStyles.row,
        ...(danger ? settingsStyles.rowDanger : {}),
      }}
      type="button"
    >
      {/* Left icon */}
      <div style={{ ...settingsStyles.rowIcon, ...(danger ? settingsStyles.rowIconDanger : {}) }}>
        {icon}
      </div>

      {/* Label + optional value */}
      <div style={settingsStyles.rowBody}>
        <span style={{ ...settingsStyles.rowLabel, ...(danger ? settingsStyles.rowLabelDanger : {}) }}>
          {label}
        </span>
        {value && (
          <span style={settingsStyles.rowValue}>{value}</span>
        )}
      </div>

      {/* Right: badge or chevron */}
      {badge ? (
        <span style={{ ...settingsStyles.badge, ...badgeStyle }}>{badge}</span>
      ) : (
        <span style={settingsStyles.chevron}>
          {rightIcon || (
            <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
              <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          )}
        </span>
      )}
    </button>
  );

  /* ── JSX ──────────────────────────────────────── */
  return (
    <div className="bg-scene-light" style={settingsStyles.page}>

      {/* ── Ambient orbs ── */}
      <div style={settingsStyles.orb1} aria-hidden="true" />
      <div style={settingsStyles.orb2} aria-hidden="true" />

      {/* ── Header bar ── */}
      <header style={settingsStyles.header}>
        <button
          aria-label="Go back"
          onClick={() => navigate(-1)}
          style={settingsStyles.backBtn}
          type="button"
        >
          <svg fill="none" height="20" stroke="currentColor" viewBox="0 0 24 24" width="20">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} />
          </svg>
        </button>
        <h1 style={settingsStyles.headerTitle}>Profile</h1>
        <ThemeToggle />
      </header>

      {/* ── Scrollable body ── */}
      <div style={settingsStyles.body}>

        {/* ── Avatar card ── */}
        <div className="glass-light" style={settingsStyles.profileCard}>
          {/* Avatar circle */}
          <div style={settingsStyles.avatarWrap}>
            <div style={settingsStyles.avatar}>
              <span style={settingsStyles.avatarText}>{playerInitials}</span>
            </div>
            <div style={settingsStyles.avatarRing} aria-hidden="true" />
            <span style={settingsStyles.avatarStatusDot} aria-hidden="true" />
          </div>

          <div style={settingsStyles.profileInfo}>
            <h2 style={settingsStyles.profileName}>{playerName}</h2>
            {playerPhone && (
              <p style={settingsStyles.profilePhone}>{maskPhone(playerPhone)}</p>
            )}
          </div>
        </div>

        <div style={settingsStyles.listStack}>
          <SettingsRow
            badge={verBadgeLabel}
            badgeStyle={verBadgeStyle}
            icon={<IdIcon />}
            label="Player Identification"
            value="PAN verification status, Aadhaar card, Bank details, Documents"
            onClick={() => navigate('/verification/container')}
          />

          <SettingsRow
            icon={<BellIcon />}
            label="Manage Notifications"
            value="Manage how you want to receive updates"
            onClick={() => toast.info('Notification settings coming soon.')}
          />

          <SettingsRow
            icon={<GiftIcon />}
            label="Invite friends get rewards"
            value={referralUnlocked
              ? "View and share your referral code"
              : "Complete KYC and first Security club check-in to unlock"}
            rightIcon={<ShareIcon />}
            onClick={() => {
              if (!referralUnlocked) {
                toast.info('Complete KYC and your first Security club check-in to unlock your referral code.');
                return;
              }
              setShowReferral((v) => !v);
            }}
          />

          {referralUnlocked && showReferral && (
            <div className="glass-light" style={settingsStyles.referralPanel}>
              <p style={settingsStyles.referralLabel}>Your Referral Code</p>
              <div style={settingsStyles.referralRow}>
                <span style={settingsStyles.referralCode}>{referralCode}</span>
                <button
                  className="btn-gold"
                  disabled={referralCode === '—'}
                  onClick={handleCopyReferral}
                  style={settingsStyles.copyBtn}
                  type="button"
                >
                  {referralCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <SettingsRow
            icon={<InfoIcon />}
            label="About App"
            value="About Us, Privacy Policy, Terms & Conditions"
            onClick={() => toast.info('ChipMonk by Poker Launcher · v1.0')}
          />

          <SettingsRow
            icon={<ClubIcon />}
            label="Club Rules"
            value="Help & FAQ's"
            onClick={() => toast.info('Club rules page coming soon.')}
          />

          <div style={{ marginTop: 4 }}>
            <SettingsRow
              danger
              icon={<LogoutIcon />}
              label="Logout"
              onClick={() => setShowLogoutConfirm(true)}
            />
          </div>
        </div>

        {/* ── Bottom safe-area spacer ── */}
        <div style={{ height: 40 }} />
      </div>

      {/* ── Logout confirm sheet ── */}
      {showLogoutConfirm && (
        <div
          style={settingsStyles.sheetOverlay}
          onClick={() => setShowLogoutConfirm(false)}
          role="presentation"
        >
          <div
            className="glass-light"
            style={settingsStyles.sheet}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-title"
          >
            {/* Drag handle */}
            <div style={settingsStyles.sheetHandle} aria-hidden="true" />

            <h3 id="logout-title" style={settingsStyles.sheetTitle}>Log out?</h3>
            <p style={settingsStyles.sheetBody}>
              You'll be signed out of your account. All session data will be cleared.
            </p>

            <div style={settingsStyles.sheetActions}>
              <button
                className="btn-gold"
                onClick={handleLogout}
                style={settingsStyles.sheetBtnDanger}
                type="button"
              >
                Yes, Logout
              </button>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={settingsStyles.sheetBtnCancel}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Icon components ──────────────────────────────────────── */

const IdIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0M9 12h6m-3-3v6" />
  </svg>
);

const BellIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const GiftIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
);

const InfoIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClubIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const LogoutIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const ShareIcon = () => (
  <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

/* ── Styles ───────────────────────────────────────────────── */

const settingsStyles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    height: '100dvh',
    fontFamily: "'Poppins', 'Inter', sans-serif",
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },

  /* Ambient orbs */
  orb1: {
    position: 'fixed',
    top: -80,
    right: -80,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'radial-gradient(closest-side, var(--cm-red-soft), transparent)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  orb2: {
    position: 'fixed',
    bottom: 60,
    left: -60,
    width: 220,
    height: 220,
    borderRadius: '50%',
    background: 'radial-gradient(closest-side, var(--cm-gold-soft), transparent)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  /* Header */
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    paddingTop: 'max(14px, env(safe-area-inset-top))',
    background: 'var(--cm-surface)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    borderBottom: '1px solid var(--cm-border)',
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    background: 'var(--cm-surface-2)',
    color: 'var(--cm-text)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 150ms',
  },
  headerTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--cm-text)',
    letterSpacing: 0.2,
  },

  /* Body */
  body: {
    position: 'relative',
    zIndex: 1,
    padding: '20px 16px',
    paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
    maxWidth: 480,
    margin: '0 auto',
  },
  listStack: {
    display: 'flex',
    flexDirection: 'column',
  },

  /* Profile card */
  profileCard: {
    padding: '24px 20px 22px',
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    textAlign: 'center',
  },
  avatarWrap: {
    position: 'relative',
    flexShrink: 0,
    marginBottom: 6,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'var(--cm-gold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    position: 'relative',
    zIndex: 1,
  },
  avatarText: {
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--cm-text)',
    letterSpacing: 0.5,
  },
  avatarRing: {
    position: 'absolute',
    inset: -5,
    borderRadius: '50%',
    border: '2px solid var(--cm-gold)',
    boxShadow: 'none',
    zIndex: 0,
  },
  avatarStatusDot: {
    position: 'absolute',
    right: -1,
    bottom: 2,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: 'var(--cm-success)',
    border: '2px solid var(--cm-surface)',
    zIndex: 2,
    boxShadow: 'none',
  },
  profileInfo: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  profileName: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--cm-text)',
    whiteSpace: 'normal',
    textAlign: 'center',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  profilePhone: {
    margin: '5px 0 0',
    fontSize: 13,
    color: 'var(--cm-text-muted)',
    letterSpacing: 0.3,
    fontWeight: 500,
  },
  profileId: {
    margin: '3px 0 0',
    fontSize: 11,
    color: 'var(--cm-text-muted)',
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },

  /* Section labels */
  sectionLabel: {
    margin: '0 0 8px 4px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--cm-text-muted)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },

  /* Settings group (card wrapper) */
  group: {
    background: 'var(--cm-surface)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--cm-border)',
    borderRadius: 18,
    boxShadow: 'none',
    overflow: 'hidden',
    marginBottom: 16,
  },

  /* Individual row */
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    background: 'var(--cm-surface)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--cm-border)',
    borderRadius: 18,
    boxShadow: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 150ms',
    marginBottom: 12,
  },
  rowDanger: {
    background: 'var(--cm-danger-soft)',
    border: '1px solid var(--cm-border)',
  },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'var(--cm-gold-soft)',
    color: 'var(--cm-gold-text)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rowIconDanger: {
    background: 'var(--cm-danger-soft)',
    color: 'var(--cm-danger)',
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  rowLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--cm-text)',
  },
  rowLabelDanger: {
    color: 'var(--cm-danger)',
  },
  rowValue: {
    fontSize: 12,
    color: 'var(--cm-text-muted)',
    lineHeight: 1.35,
  },

  /* Badges */
  badge: {
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  badgeVerified: {
    background: 'var(--cm-success-soft)',
    color: 'var(--cm-success)',
    border: '1px solid transparent',
  },
  badgePending: {
    background: 'var(--cm-warning-soft)',
    color: 'var(--cm-warning)',
    border: '1px solid transparent',
  },

  /* Chevron */
  chevron: {
    color: 'var(--cm-text-muted)',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },

  /* Referral panel */
  referralPanel: {
    padding: '14px 16px 16px',
    marginTop: -4,
    marginBottom: 12,
    background: 'var(--cm-surface)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--cm-border)',
    borderRadius: 18,
    boxShadow: 'none',
  },
  referralLabel: {
    margin: '0 0 8px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--cm-text-muted)',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  referralRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 10,
  },
  referralCode: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 12,
    background: 'var(--cm-surface)',
    border: '1px solid var(--cm-border)',
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--cm-text)',
    letterSpacing: 2,
    fontVariantNumeric: 'tabular-nums',
  },
  copyBtn: {
    width: 'auto',
    minWidth: 84,
    padding: '10px 14px',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
  },

  /* Logout confirm sheet */
  sheetOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    background: 'var(--cm-overlay)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    animation: 'gl-fade-in 200ms ease-out',
  },
  sheet: {
    width: '100%',
    maxWidth: 480,
    borderRadius: '24px 24px 0 0',
    padding: '0 20px 20px',
    paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
    animation: 'gl-fade-up 250ms ease-out',
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: 'var(--cm-surface-2)',
    margin: '12px auto 16px',
  },
  sheetTitle: {
    margin: '0 0 8px',
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--cm-text)',
  },
  sheetBody: {
    margin: '0 0 20px',
    fontSize: 14,
    color: 'var(--cm-text-muted)',
    lineHeight: 1.5,
  },
  sheetActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sheetBtnDanger: {
    padding: '15px',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    background: 'var(--cm-danger)',
    color: '#fff',
    borderRadius: 50,
    boxShadow: 'none',
  },
  sheetBtnCancel: {
    padding: '14px',
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid var(--cm-border)',
    background: 'var(--cm-surface-2)',
    color: 'var(--cm-gold-text)',
    borderRadius: 50,
    cursor: 'pointer',
  },
};

export default PlayerSettings;
